import React, { createContext, useState, useEffect, useContext } from 'react';

const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export const CurrencyProvider = ({ children }) => {
  // Get currency from localStorage or default to null (will be set by geolocation)
  const [currency, setCurrency] = useState(() => {
    const saved = localStorage.getItem('selectedCurrency');
    return saved || null; // null means we need to detect
  });
  
  const [isDetecting, setIsDetecting] = useState(!currency);

  // Exchange rate: 1 BHD = 2.65 USD
  const EXCHANGE_RATE = 2.65;

  // Detect user location and set currency
  useEffect(() => {
    const detectCurrency = async () => {
      // If currency already saved, don't detect again
      if (localStorage.getItem('selectedCurrency')) {
        setIsDetecting(false);
        return;
      }

      try {
        // Try to get location from IP-based geolocation service
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        
        // Check if user is in Bahrain
        if (data.country_code === 'BH') {
          setCurrency('BHD');
          localStorage.setItem('selectedCurrency', 'BHD');
        } else {
          // Default to USD for non-Bahrain users
          setCurrency('USD');
          localStorage.setItem('selectedCurrency', 'USD');
        }
      } catch (error) {
        console.error('Error detecting location:', error);
        // Fallback: check browser's timezone for Bahrain
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (timezone === 'Asia/Bahrain') {
          setCurrency('BHD');
          localStorage.setItem('selectedCurrency', 'BHD');
        } else {
          // Default to USD if detection fails
          setCurrency('USD');
          localStorage.setItem('selectedCurrency', 'USD');
        }
      } finally {
        setIsDetecting(false);
      }
    };

    if (!currency) {
      detectCurrency();
    }
  }, [currency]);

  const changeCurrency = (newCurrency) => {
    setCurrency(newCurrency);
    localStorage.setItem('selectedCurrency', newCurrency);
  };

  // Convert price based on selected currency
  const getPrice = (productPrice) => {
    if (!productPrice) return { display: '0.00', value: 0, currency: currency || 'BHD' };

    let bhdPrice = 0;
    let usdPrice = 0;

    // Handle dual pricing structure
    if (typeof productPrice === 'object') {
      const rawBhd = productPrice.bhd ?? productPrice.BHD;
      const rawUsd = productPrice.usd ?? productPrice.USD;

      bhdPrice = parseFloat(rawBhd) || 0;
      usdPrice = parseFloat(rawUsd) || 0;

      if (bhdPrice > 0 && usdPrice <= 0) {
        usdPrice = parseFloat((bhdPrice * EXCHANGE_RATE).toFixed(2));
      }

      if (usdPrice > 0 && bhdPrice <= 0) {
        bhdPrice = parseFloat((usdPrice / EXCHANGE_RATE).toFixed(2));
      }
    } else {
      // Legacy single price - assume it's BHD
      bhdPrice = parseFloat(productPrice) || 0;
      usdPrice = parseFloat((bhdPrice * EXCHANGE_RATE).toFixed(2));
    }

    if (currency === 'USD') {
      return {
        display: `$${parseFloat(usdPrice).toFixed(2)}`,
        value: parseFloat(usdPrice),
        currency: 'USD',
        bhd: bhdPrice
      };
    } else {
      return {
        display: `BHD ${parseFloat(bhdPrice).toFixed(2)}`,
        value: bhdPrice,
        currency: 'BHD',
        usd: parseFloat(usdPrice)
      };
    }
  };

  // Format price for display
  const formatPrice = (price) => {
    return getPrice(price).display;
  };

  // Get raw price value for calculations
  const getPriceValue = (price) => {
    return getPrice(price).value;
  };

  /** Format a BHD-only amount (shipping, totals) in the selected currency. */
  const formatAmountFromBhd = (bhdAmount) => {
    const bhd = parseFloat(bhdAmount) || 0;
    if (currency === 'USD') {
      return `$${(bhd * EXCHANGE_RATE).toFixed(2)}`;
    }
    return `BHD ${bhd.toFixed(2)}`;
  };

  /** Convert a BHD amount to the selected currency's numeric value. */
  const convertFromBhd = (bhdAmount) => {
    const bhd = parseFloat(bhdAmount) || 0;
    if (currency === 'USD') {
      return Number((bhd * EXCHANGE_RATE).toFixed(2));
    }
    return bhd;
  };

  const value = {
    currency,
    changeCurrency,
    getPrice,
    formatPrice,
    getPriceValue,
    formatAmountFromBhd,
    convertFromBhd,
    isDetecting,
    EXCHANGE_RATE
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export default CurrencyContext;

